import React, { useRef, useState } from 'react';
import api from '../../api/client';
import useForm from '../../hooks/useForm';
import { trackEvent } from '../../api/tracker';
import { PROGRAMME_ROUTES } from '../../data/programmeRoutes';
import { withPrefill, internshipSample } from '../../utils/devPrefill';
import {
  required, email as emailRule, phone as phoneRule, url as urlRule,
  name as nameRule, minLength, maxLength, numberBetween, fileSize, fileType,
} from '../../utils/validators';

const CURRENT_YEAR = new Date().getFullYear();

const SCHEMA = {
  first_name: [required('Enter your first name'), nameRule(), maxLength(80)],
  last_name: [required('Enter your last name'), nameRule(), maxLength(80)],
  email: [required('Enter your email address'), emailRule()],
  phone: [required('Enter your phone number'), phoneRule()],
  college: [required('Enter your college or university'), minLength(3), maxLength(200)],
  programme_type: [required('Choose how you want to join')],
  track_slug: [required('Choose the track you want to apply for')],
  graduation_year: [numberBetween(2000, CURRENT_YEAR + 6, 'Enter a valid graduation year')],
  cgpa: [numberBetween(0, 10, 'CGPA must be between 0 and 10')],
  portfolio_url: [urlRule()],
  linkedin_url: [urlRule()],
  github_url: [urlRule()],
  cover_note: [maxLength(3000)],
  resume: [
    required('Attach your resume'),
    fileType(['.pdf', '.doc', '.docx'], 'Upload a PDF, DOC or DOCX file'),
    fileSize(5, 'That resume is larger than 5 MB'),
  ],
  consent: [required('Tick the box to confirm your details are accurate')],
};

const EMPTY = {
  first_name: '', last_name: '', email: '', phone: '', city: '', state: '',
  college: '', degree: '', branch: '', graduation_year: '', cgpa: '',
  programme_type: 'internship',
  track_slug: '', duration: '', preferred_start: '', work_mode: 'onsite',
  portfolio_url: '', linkedin_url: '', github_url: '', cover_note: '',
  heard_from: '', resume: null, consent: false,
  company_website: '', // honeypot
};

/** Small helper so every field renders its error the same way. */
function Field({ label, name, error, required: isRequired, hint, children }) {
  return (
    <div className="sp-field">
      <label className="sp-label" htmlFor={name}>
        {label}
        {isRequired ? <span className="sp-req" aria-hidden="true">*</span>
          : <span className="sp-optional"> (optional)</span>}
      </label>
      {children}
      {error && (
        <p className="sp-error" id={`${name}-error`} role="alert">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          {error}
        </p>
      )}
      {!error && hint && <p className="sp-hint">{hint}</p>}
    </div>
  );
}

export default function InternshipApplicationForm({ tracks = [], preselectedTrack = '' }) {
  const [reference, setReference] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  const form = useForm({
    // Empty in production; test data when REACT_APP_PREFILL_FORMS=true locally.
    initialValues: withPrefill(
      { ...EMPTY, track_slug: preselectedTrack },
      () => ({ ...internshipSample(), ...(preselectedTrack ? { track_slug: preselectedTrack } : {}) }),
    ),
    schema: SCHEMA,
    formName: 'internship_application',
    page: 'training-internship',
    resetOnSuccess: false, // we swap the form for a confirmation instead
    onSubmit: async (values) => {
      // multipart, because a resume file rides along with the fields
      const payload = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (key === 'resume' || key === 'skills') return;
        if (value === '' || value == null) return;
        payload.append(key, typeof value === 'boolean' ? String(value) : value);
      });

      payload.append('skills', JSON.stringify(selectedSkills));
      payload.append('source_page', 'training-internship');
      if (values.resume) payload.append('resume', values.resume);

      const { data } = await api.post('/api/internships/apply', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setReference(data?.data?.reference_code || null);
      trackEvent('application_submitted', {
        label: values.track_slug, page: 'training-internship', category: 'conversion',
      });
      return data;
    },
  });

  const { values, errors, isSubmitting, handleSubmit, fieldProps, setValue } = form;

  // Skills are chips rather than a text field, so they live outside useForm's
  // value map and are appended at submit time.
  const [selectedSkills, setSelectedSkills] = useState([]);
  const activeTrack = tracks.find((t) => t.slug === values.track_slug);
  // Only the selected track's skills are offered. Across ten tracks there are
  // now 85 of them, and an arbitrary slice of that union is noise — a marketing
  // applicant should not be scrolling past TensorFlow to find Google Ads.
  const skillOptions = activeTrack?.skills || [];

  // Switching track replaces the chip list, so drop any selection that is no
  // longer on offer rather than silently submitting it.
  React.useEffect(() => {
    setSelectedSkills((prev) => prev.filter((s) => skillOptions.includes(s)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.track_slug]);

  const toggleSkill = (skill) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const attachFile = (file) => {
    setValue('resume', file || null);
    if (file) {
      trackEvent('resume_upload', { label: file.type, page: 'training-internship' });
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) attachFile(file);
  };

  // ---------------------------------------------------------------- success
  if (reference) {
    return (
      <div className="sp-form-card">
        <div className="sp-done">
          <div className="sp-done__icon" aria-hidden="true">
            <i className="fa-solid fa-check" />
          </div>
          <h3 className="sp-heading" style={{ fontSize: '1.6rem' }}>Application received</h3>
          <p className="sp-lead" style={{ margin: '0 auto' }}>
            We have emailed a copy to {values.email}. Quote this reference if you need to follow up.
          </p>
          <div className="sp-done__ref">{reference}</div>
          <p className="sp-hint" style={{ maxWidth: '46ch', margin: '0 auto' }}>
            We screen applications every Monday and Thursday. Shortlisted candidates hear from us
            within 7 working days.
          </p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------- form
  return (
    <div className="sp-form-card">
      <form onSubmit={handleSubmit} noValidate>
        {/* Bots fill every field they find; people never see this one. */}
        <div className="sp-honeypot" aria-hidden="true">
          <label htmlFor="company_website">Do not fill this in</label>
          <input
            type="text" id="company_website" name="company_website" tabIndex={-1}
            autoComplete="off" value={values.company_website} onChange={form.handleChange}
          />
        </div>

        {/* ---------------------------------------------------------- 1 */}
        <fieldset className="sp-fieldset">
          <legend><span>1</span> About you</legend>
          <div className="row">
            <div className="col-md-6">
              <Field label="First name" name="first_name" error={errors.first_name} required>
                <input className="sp-input" id="first_name" type="text"
                  placeholder="Aarav" autoComplete="given-name" {...fieldProps('first_name')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Last name" name="last_name" error={errors.last_name} required>
                <input className="sp-input" id="last_name" type="text"
                  placeholder="Sharma" autoComplete="family-name" {...fieldProps('last_name')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Email" name="email" error={errors.email} required
                hint="We send your reference number and all updates here.">
                <input className="sp-input" id="email" type="email"
                  placeholder="aarav@example.com" autoComplete="email" {...fieldProps('email')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Phone" name="phone" error={errors.phone} required>
                <input className="sp-input" id="phone" type="tel"
                  placeholder="98765 43210" autoComplete="tel" {...fieldProps('phone')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="City" name="city" error={errors.city}>
                <input className="sp-input" id="city" type="text"
                  placeholder="Jaipur" autoComplete="address-level2" {...fieldProps('city')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="State" name="state" error={errors.state}>
                <input className="sp-input" id="state" type="text"
                  placeholder="Rajasthan" autoComplete="address-level1" {...fieldProps('state')} />
              </Field>
            </div>
          </div>
        </fieldset>

        {/* ---------------------------------------------------------- 2 */}
        <fieldset className="sp-fieldset">
          <legend><span>2</span> Education</legend>
          <div className="row">
            <div className="col-12">
              <Field label="College or university" name="college" error={errors.college} required>
                <input className="sp-input" id="college" type="text"
                  placeholder="MNIT Jaipur" {...fieldProps('college')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Degree" name="degree" error={errors.degree}>
                <select className="sp-select" id="degree" {...fieldProps('degree')}>
                  <option value="">Select</option>
                  {['B.Tech', 'B.E.', 'BCA', 'B.Sc', 'B.Com', 'BBA', 'M.Tech', 'MCA', 'MBA', 'M.Sc', 'Diploma', 'Other']
                    .map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Branch or stream" name="branch" error={errors.branch}>
                <input className="sp-input" id="branch" type="text"
                  placeholder="Computer Science" {...fieldProps('branch')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Graduation year" name="graduation_year" error={errors.graduation_year}>
                <select className="sp-select" id="graduation_year" {...fieldProps('graduation_year')}>
                  <option value="">Select</option>
                  {Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - 2 + i)
                    .map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="CGPA or percentage" name="cgpa" error={errors.cgpa}
                hint="On a 10-point scale.">
                <input className="sp-input" id="cgpa" type="number" step="0.01" min="0" max="10"
                  placeholder="8.20" {...fieldProps('cgpa')} />
              </Field>
            </div>
          </div>
        </fieldset>

        {/* ---------------------------------------------------------- 3 */}
        <fieldset className="sp-fieldset">
          <legend><span>3</span> Programme</legend>
          <div className="row">
            <div className="col-12">
              <Field label="How do you want to join" name="programme_type"
                error={errors.programme_type} required>
                <div className="sp-routes" role="radiogroup" aria-label="Programme route">
                  {PROGRAMME_ROUTES.map((route) => (
                    <label
                      key={route.value}
                      className={`sp-route${values.programme_type === route.value ? ' is-on' : ''}`}
                    >
                      <input
                        type="radio"
                        name="programme_type"
                        value={route.value}
                        checked={values.programme_type === route.value}
                        onChange={form.handleChange}
                      />
                      <span className="sp-route__title">{route.label}</span>
                      <span className="sp-route__summary">{route.summary}</span>
                      <span className="sp-route__desc">{route.description}</span>
                    </label>
                  ))}
                </div>
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Track" name="track_slug" error={errors.track_slug} required>
                <select className="sp-select" id="track_slug" {...fieldProps('track_slug')}>
                  <option value="">Choose a track</option>
                  {tracks.map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {t.title}{t.seats ? ` — ${t.seats} seats` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Preferred start" name="preferred_start" error={errors.preferred_start}>
                <input className="sp-input" id="preferred_start" type="date"
                  min={new Date().toISOString().slice(0, 10)} {...fieldProps('preferred_start')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Work mode" name="work_mode" error={errors.work_mode}>
                <select className="sp-select" id="work_mode" {...fieldProps('work_mode')}>
                  <option value="onsite">Onsite — Jaipur</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="remote">Remote</option>
                </select>
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="How did you hear about us" name="heard_from" error={errors.heard_from}>
                <select className="sp-select" id="heard_from" {...fieldProps('heard_from')}>
                  <option value="">Select</option>
                  {['College / TPO', 'LinkedIn', 'Instagram', 'Internshala', 'Friend or referral',
                    'Google search', 'Job fair', 'Other'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            <div className="col-12">
              <div className="sp-field">
                <span className="sp-label">
                  Skills you already have <span className="sp-optional">(optional)</span>
                </span>
                {skillOptions.length === 0 && (
                  <p className="sp-hint" style={{ marginTop: 0 }}>
                    Choose a track above and the relevant skills appear here.
                  </p>
                )}
                  <div className="sp-chips" role="group" aria-label="Skills">
                    {skillOptions.map((skill) => (
                      <button
                        key={skill}
                        type="button"
                        className={`sp-skill${selectedSkills.includes(skill) ? ' is-on' : ''}`}
                        aria-pressed={selectedSkills.includes(skill)}
                        onClick={() => toggleSkill(skill)}
                      >
                        {skill}
                      </button>
                    ))}
                  </div>
                {skillOptions.length > 0 && (
                  <p className="sp-hint">
                    Pick what applies. We train you on the rest — gaps are not a reason not to
                    apply, and the Training + Internship route exists for exactly that.
                  </p>
                )}
              </div>
            </div>
          </div>
        </fieldset>

        {/* ---------------------------------------------------------- 4 */}
        <fieldset className="sp-fieldset">
          <legend><span>4</span> Your work</legend>
          <div className="row">
            <div className="col-12">
              <Field label="Resume" name="resume" error={errors.resume} required>
                <div
                  className={`sp-drop${dragging ? ' is-drag' : ''}${values.resume ? ' has-file' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  onClick={() => fileInput.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current?.click(); }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-describedby={errors.resume ? 'resume-error' : undefined}
                >
                  <i className={`fa-solid ${values.resume ? 'fa-file-circle-check' : 'fa-cloud-arrow-up'}`} aria-hidden="true" />
                  <div className="sp-drop__main">
                    {values.resume ? values.resume.name : 'Drop your resume here, or click to browse'}
                  </div>
                  <div className="sp-drop__sub">
                    {values.resume
                      ? `${(values.resume.size / 1024 / 1024).toFixed(2)} MB — click to replace`
                      : 'PDF, DOC or DOCX, up to 5 MB'}
                  </div>
                </div>
                <input
                  ref={fileInput} id="resume" name="resume" type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={(e) => attachFile(e.target.files?.[0])}
                />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="LinkedIn" name="linkedin_url" error={errors.linkedin_url}>
                <input className="sp-input" id="linkedin_url" type="url"
                  placeholder="linkedin.com/in/you" {...fieldProps('linkedin_url')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="GitHub" name="github_url" error={errors.github_url}>
                <input className="sp-input" id="github_url" type="url"
                  placeholder="github.com/you" {...fieldProps('github_url')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Portfolio" name="portfolio_url" error={errors.portfolio_url}>
                <input className="sp-input" id="portfolio_url" type="url"
                  placeholder="yoursite.com" {...fieldProps('portfolio_url')} />
              </Field>
            </div>
            <div className="col-12">
              <Field label="Why this track" name="cover_note" error={errors.cover_note}
                hint="A short paragraph beats a long one. Tell us what you want to build or learn here.">
                <textarea className="sp-textarea" id="cover_note" rows={4}
                  placeholder="I want to work on exam-day operations because…" {...fieldProps('cover_note')} />
              </Field>
            </div>
          </div>
        </fieldset>

        {/* --------------------------------------------------------- send */}
        <div className="sp-field">
          <div className="sp-check">
            <input
              type="checkbox" id="consent" name="consent"
              checked={values.consent} onChange={form.handleChange}
              aria-invalid={Boolean(errors.consent) || undefined}
            />
            <label htmlFor="consent">
              Everything above is accurate, and Starparth Technologies may contact me about this
              application and store my details for the current intake.
            </label>
          </div>
          {errors.consent && (
            <p className="sp-error" id="consent-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              {errors.consent}
            </p>
          )}
        </div>

        <button type="submit" className="sp-btn sp-btn--primary" disabled={isSubmitting}
          style={{ width: '100%', justifyContent: 'center', padding: '15px' }}>
          {isSubmitting ? (
            <><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Sending your application…</>
          ) : (
            <>Send application <i className="fa-solid fa-arrow-right" aria-hidden="true" /></>
          )}
        </button>

        <p className="sp-hint" style={{ textAlign: 'center', marginTop: '14px' }}>
          Applications are free. We never charge a fee at any stage.
        </p>
      </form>
    </div>
  );
}
